"use client";

import { useEffect } from "react";

/**
 * Writes the live `window.visualViewport` metrics to CSS custom properties on
 * the document root, falling back gracefully where `visualViewport` is
 * unsupported:
 *   --vvh            → visual viewport height (px)
 *   --vv-offset-top  → visual viewport offsetTop (px)
 *
 * Layouts that pin content to the bottom of the screen (e.g. a chat input)
 * should size themselves with `var(--vvh, 100dvh)` instead of `100dvh` so they
 * track the real usable height when the on-screen keyboard opens (iOS Safari
 * and Android don't shrink `dvh`/`vh` reliably while the keyboard is up).
 *
 * A `fixed` overlay should additionally translate by `var(--vv-offset-top)` so
 * it follows the visual viewport when iOS scrolls the layout viewport to reveal
 * a focused input — this is what stops the "whole thing flies up and goes
 * blank" behaviour on iOS.
 *
 * `active` re-triggers the initial measurement — needed for a surface (like
 * the guest-chat simulator) that's mounted once and toggled via an `open`
 * prop rather than mounted fresh each time it's shown: without this, the one
 * mount-time measurement happens while the surface is still hidden/hasn't
 * settled, leaving stale (often too-tall) values until the next resize/scroll
 * event — e.g. focusing the input — happens to correct it.
 */
export function useVisualViewportHeight(active: boolean = true) {
  useEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const root = document.documentElement;
    const update = () => {
      // When the keyboard is closed, visualViewport.height should equal
      // window.innerHeight — but on a real device the resize events fired
      // while the keyboard animates open/closed can settle with a slightly
      // stale non-zero offsetTop (or a height a few px short of full), and
      // nothing ever fires again to correct it since the keyboard is done
      // moving. A `fixed` overlay reading a stuck --vv-offset-top then stays
      // translated down indefinitely — its own header scrolls out of view
      // above (revealing whatever's behind it) while a gap opens up at the
      // bottom, even though the keyboard has been closed the whole time. So:
      // once nothing looks keyboard-occluded, snap both vars back to their
      // canonical "fully open" values instead of trusting whatever transient
      // numbers this particular event reported.
      const keyboardClosed = Math.abs(vv.height - window.innerHeight) < 2;
      root.style.setProperty(
        "--vvh",
        `${keyboardClosed ? window.innerHeight : vv.height}px`,
      );
      root.style.setProperty(
        "--vv-offset-top",
        keyboardClosed ? "0px" : `${vv.offsetTop}px`,
      );
      // Cancel WebKit's own layout-viewport scroll-into-view on focus — without
      // this, WebKit's scroll and this hook's --vv-offset-top compensation both
      // apply, overshooting and leaving a gap above the keyboard.
      window.scrollTo(0, 0);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      // Intentionally leave --vvh / --vv-offset-top on :root — the last known
      // values are harmless as a fallback, and clearing them could momentarily
      // break another chat surface that shares the same custom properties.
    };
  }, [active]);
}
